import React, { useState, useEffect } from 'react';
import Iconify from 'src/components/Iconify';
import { Card } from 'src/@/components/ui/card';
import useAuth from 'src/hooks/useAuth';
import propostasService from 'src/services/propostasService';
import { PROPOSTAS_DESIGN } from './Propostas.design';

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

function formatPercent(value) {
  return `${Math.round((value || 0) * 100)}%`;
}

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export default function RelatoriosPropostas() {
  const { user } = useAuth();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.clienteId) loadRelatorio();
  }, [user?.clienteId]);

  const loadRelatorio = async () => {
    try {
      setLoading(true);
      const response = await propostasService.relatorios(user?.clienteId);
      if (!response.error) setDados(response.data);
    } catch (error) {
      console.error('Erro ao carregar relatório de propostas:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-2xl shadow-sm border border-gray-100 dark:border-[#383838] dark:bg-[#222529] overflow-hidden">
        <div className="flex flex-col items-center justify-center h-64 text-gray-400 dark:text-[#ABABAD]">
          <Iconify icon="mdi:loading" className="w-8 h-8 animate-spin mb-4 text-[#7C3AED]" />
          <p className="text-sm font-medium">Carregando relatório...</p>
        </div>
      </Card>
    );
  }

  if (!dados) return null;

  const maiorValorMes = Math.max(1, ...dados.serieMensal.map(m => m.valor || 0));

  const kpis = [
    { icon: 'mdi:send-outline', label: 'Propostas enviadas', value: dados.enviadas },
    { icon: 'mdi:eye-outline', label: 'Taxa de visualização', value: formatPercent(dados.taxaVisualizacao) },
    { icon: 'mdi:check-circle-outline', label: 'Taxa de aceite', value: formatPercent(dados.taxaAceite) },
    { icon: 'mdi:cash-multiple', label: 'Ticket médio (aceitas)', value: formatCurrency(dados.ticketMedio) },
    { icon: 'mdi:clock-outline', label: 'Tempo médio até aceite', value: dados.tempoMedioAceiteDias ? `${dados.tempoMedioAceiteDias.toFixed(1)} dias` : '-' },
    { icon: 'mdi:cash-check', label: 'Valor total aceito', value: formatCurrency(dados.valorTotalAceito) }
  ];

  return (
    <div className="space-y-4">
      <div className={PROPOSTAS_DESIGN.stats.grid}>
        {kpis.map(kpi => (
          <div key={kpi.label} className={PROPOSTAS_DESIGN.stats.card}>
            <div className={PROPOSTAS_DESIGN.stats.iconBox}>
              <Iconify icon={kpi.icon} width={20} className={PROPOSTAS_DESIGN.stats.icon} />
            </div>
            <div>
              <p className={PROPOSTAS_DESIGN.stats.value}>{kpi.value}</p>
              <p className={PROPOSTAS_DESIGN.stats.label}>{kpi.label}</p>
            </div>
          </div>
        ))}
      </div>

      <Card className="rounded-2xl border border-gray-100 dark:border-[#383838] dark:bg-[#222529] p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3] mb-4">Valor proposto por mês</h3>
        {dados.serieMensal.length === 0 ? (
          <p className="text-sm text-gray-400">Sem dados no período.</p>
        ) : (
          <div className="flex items-end gap-3 h-40">
            {dados.serieMensal.map((m, i) => (
              <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1.5">
                <span className="text-[10px] text-gray-500 dark:text-[#ABABAD]">{formatCurrency(m.valor)}</span>
                <div
                  className="w-full rounded-t-md bg-[#7C3AED]/80"
                  style={{ height: `${Math.max(4, (m.valor / maiorValorMes) * 100)}%` }}
                />
                <span className="text-[10px] text-gray-400">{MESES[m._id.mes - 1]}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
